import { useEffect, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { configAppService } from "@/services/configAppService";
import type { ConfigAppPayload } from "@/types/configApp";
import { ConfigNotifTab, ConfigServicesTab, ConfigStaticTab } from "./ConfigIntegrationTabs";

// UI Components
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton"; // PASTIKAN IMPORT INI ADA
import {
    Settings,
    Building2,
    FileText,
    Globe,
    Lock,
    Wrench,
    CalendarClock,
    Save,
    AlertCircle,
    UserPlus,
    CheckCircle2,
    XCircle,
    Loader2
} from "lucide-react";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, Plug, Server } from "lucide-react";

// --- HELPER FUNCTIONS ---
const formatToInputDate = (dateString: string | null) => {
    if (!dateString) return "";
    return dateString.substring(0, 19).replace(" ", "T");
};

const formatToLaravelDate = (dateString: string) => {
    if (!dateString) return null;
    return dateString.length > 16
        ? dateString.replace("T", " ")
        : dateString.replace("T", " ") + ":00";
};

// --- SCHEMA VALIDATION ---
const formSchema = z.object({
    app_name: z.string().min(1, "Nama aplikasi wajib diisi"),
    app_institution: z.string().min(1, "Institusi wajib diisi"),
    app_description: z.string().optional(),
    registration_open: z.boolean().default(false),
    site_status: z.enum(["public", "private", "maintenance"]),
    site_status_until: z.string().min(1, "Batas waktu wajib diisi (ketentuan sistem)"),
});

type FormValues = z.infer<typeof formSchema>;

export default function SettingAppPage() {
    // State management
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            app_name: "",
            app_institution: "",
            app_description: "",
            registration_open: false,
            site_status: "public",
            site_status_until: "",
        },
    });

    // Fetch Data
    useEffect(() => {
        let isMounted = true; // Mencegah update state jika komponen sudah unmount

        const fetchData = async () => {
            // Kita tidak set setIsLoading(true) di sini agar skeleton langsung muncul saat mount pertama kali
            try {
                const data = await configAppService.getConfig();

                if (isMounted) {
                    // Reset form dengan data dari API
                    form.reset({
                        app_name: data.app_name,
                        app_institution: data.app_institution,
                        app_description: data.app_description || "",
                        registration_open: Boolean(data.registration_open),
                        site_status: data.site_status,
                        site_status_until: formatToInputDate(data.site_status_until),
                    });
                }
            } catch (error) {
                console.error(error);
                if (isMounted) toast.error("Gagal mengambil data pengaturan.");
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [form]); // Dependency 'form' aman karena useForm hook stabil

    const onSubmit = async (values: FormValues) => {
        setIsSaving(true);
        const toastId = toast.loading("Menyimpan perubahan...");

        try {
            const payload: ConfigAppPayload = {
                ...values,
                app_description: values.app_description || "",
                site_status_until: formatToLaravelDate(values.site_status_until || ""),
            };

            await configAppService.updateConfig(payload);

            toast.success("Pengaturan Berhasil Disimpan!", { id: toastId });

            // Refresh data spesifik untuk format tanggal (opsional, untuk memastikan sinkronisasi)
            const updatedData = await configAppService.getConfig();
            form.setValue("site_status_until", formatToInputDate(updatedData.site_status_until));

        } catch (error) {
            console.error(error);
            toast.dismiss(toastId);

            if (error instanceof Error && 'response' in error) {
                const axiosError = error as { response?: { status: number; data: { errors: Record<string, string[]> } } };
                if (axiosError.response?.status === 422) {
                    const errors = axiosError.response.data.errors;
                    Object.keys(errors).forEach((key) => {
                        form.setError(key as keyof FormValues, { message: errors[key][0] });
                    });
                    toast.error("Terdapat kesalahan input.");
                } else {
                    toast.error("Gagal menyimpan perubahan.");
                }
            } else {
                toast.error("Terjadi kesalahan sistem.");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const onInvalid = (errors: FieldErrors<FormValues>) => {
        if (errors.site_status_until) {
            toast.error("Gagal Simpan! Mohon isi tanggal batas waktu.");
        } else {
            toast.error("Mohon lengkapi formulir dengan benar.");
        }
    };

    // --- SKELETON COMPONENT ---
    // Ditampilkan saat isLoading === true
    if (isLoading) {
        return (
            <section className="bg-gradient-to-b from-muted/30 to-background dark:from-gray-900/30 dark:to-gray-900 min-h-screen">
                <div className="container mx-auto">
                    <Card className="bg-white/90 shadow-2xl border-0 dark:bg-gray-800/50 pt-0 overflow-hidden">
                        <div className="h-32 bg-gray-200 dark:bg-gray-700 animate-pulse" /> {/* Header Skeleton */}
                        <CardContent className="p-4 md:p-8 space-y-10">
                            {/* Identitas Skeleton */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <Skeleton className="h-10 w-10 rounded-full" />
                                    <Skeleton className="h-8 w-48" />
                                </div>
                                <div className="h-px bg-gray-200 dark:bg-gray-700 w-full mb-6"></div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <Skeleton className="h-5 w-32" />
                                        <Skeleton className="h-12 w-full rounded-lg" />
                                    </div>
                                    <div className="space-y-3">
                                        <Skeleton className="h-5 w-32" />
                                        <Skeleton className="h-12 w-full rounded-lg" />
                                    </div>
                                </div>
                                <div className="space-y-3 mt-4">
                                    <Skeleton className="h-5 w-32" />
                                    <Skeleton className="h-24 w-full rounded-lg" />
                                </div>
                            </div>

                            {/* Status Skeleton */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <Skeleton className="h-10 w-10 rounded-full" />
                                    <Skeleton className="h-8 w-48" />
                                </div>
                                <div className="h-px bg-gray-200 dark:bg-gray-700 w-full mb-6"></div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Skeleton className="h-32 w-full rounded-xl" />
                                    <Skeleton className="h-32 w-full rounded-xl" />
                                    <Skeleton className="h-32 w-full rounded-xl" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                                    <div className="space-y-3">
                                        <Skeleton className="h-5 w-40" />
                                        <Skeleton className="h-14 w-full rounded-lg" />
                                    </div>
                                    <div className="space-y-3">
                                        <Skeleton className="h-5 w-40" />
                                        <Skeleton className="h-14 w-full rounded-lg" />
                                    </div>
                                </div>
                            </div>
                            {/* Button Skeleton */}
                            <Skeleton className="h-14 w-full rounded-xl mt-6" />
                        </CardContent>
                    </Card>
                </div>
            </section>
        );
    }

    // --- MAIN RENDER ---
    return (
        <section className="bg-gradient-to-b from-muted/30 to-background dark:from-gray-900/30 dark:to-gray-900 min-h-screen">
            <div className="container mx-auto">
                <div className="lg:col-span-3 order-1 lg:order-2">
                    <Card className="bg-white/90 shadow-2xl border-0 dark:bg-gray-800/50 dark:border-gray-700 pt-0 overflow-hidden">

                        <CardHeader className="bg-gradient-to-r from-primary to-primary dark:from-blue-600 dark:to-purple-600 text-white p-8">
                            <CardTitle className="text-xl md:text-2xl lg:text-3xl flex items-center gap-3">
                                <Settings className="w-6 h-6 md:w-8 md:h-8" />
                                Pengaturan Aplikasi
                            </CardTitle>
                            <CardDescription className="text-white/90 text-sm md:text-base lg:text-lg">
                                Kelola identitas aplikasi, status akses, dan pendaftaran pengguna.
                            </CardDescription>
                        </CardHeader>

                        <CardContent className="p-4 md:p-8">
                            <Tabs defaultValue="identitas" className="w-full">
                                <TabsList className="mb-6 bg-slate-100 dark:bg-slate-800/50 p-1.5 rounded-xl w-full flex flex-wrap h-auto justify-start border border-slate-200 dark:border-slate-700">
                                    <TabsTrigger value="identitas" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-5 py-2.5">
                                        <Settings className="w-4 h-4 mr-1.5" /> Identitas & Status
                                    </TabsTrigger>
                                    <TabsTrigger value="notif" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-5 py-2.5">
                                        <Bell className="w-4 h-4 mr-1.5" /> Notifikasi (SSO)
                                    </TabsTrigger>
                                    <TabsTrigger value="services" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-5 py-2.5">
                                        <Plug className="w-4 h-4 mr-1.5" /> Layanan Eksternal
                                    </TabsTrigger>
                                    <TabsTrigger value="static" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-5 py-2.5">
                                        <Server className="w-4 h-4 mr-1.5" /> Server Statis
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="identitas" className="mt-0 outline-none focus:outline-none">
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-8 md:space-y-10">

                                    {/* === BAGIAN 1: IDENTITAS APLIKASI === */}
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                                                <Building2 className="w-4 h-4 md:w-5 md:h-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <h3 className="text-lg md:text-xl font-bold text-primary dark:text-blue-400">
                                                Identitas Aplikasi
                                            </h3>
                                        </div>
                                        <div className="h-px bg-gray-200 dark:bg-gray-700 w-full mb-6"></div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                            <FormField
                                                control={form.control}
                                                name="app_name"
                                                render={({ field }) => (
                                                    <FormItem className="space-y-3">
                                                        <FormLabel className="text-sm md:text-base font-semibold">Nama Aplikasi <span className="text-red-500">*</span></FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                placeholder="Contoh: Konaspi XII"
                                                                className="h-10 md:h-12 text-sm md:text-base border-gray-300 focus:border-primary focus:ring-primary dark:bg-gray-700 dark:border-gray-600"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="app_institution"
                                                render={({ field }) => (
                                                    <FormItem className="space-y-3">
                                                        <FormLabel className="text-sm md:text-base font-semibold">Institusi Penyelenggara <span className="text-red-500">*</span></FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                placeholder="Contoh: Universitas Pendidikan Ganesha"
                                                                className="h-10 md:h-12 text-sm md:text-base border-gray-300 focus:border-primary focus:ring-primary dark:bg-gray-700 dark:border-gray-600"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>

                                        <FormField
                                            control={form.control}
                                            name="app_description"
                                            render={({ field }) => (
                                                <FormItem className="space-y-3">
                                                    <FormLabel className="text-sm md:text-base font-semibold flex items-center gap-2">
                                                        <FileText className="w-4 h-4" /> Deskripsi
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Textarea
                                                            placeholder="Deskripsi singkat tentang aplikasi atau event ini..."
                                                            className="min-h-[100px] text-sm md:text-base resize-none border-gray-300 focus:border-primary focus:ring-primary dark:bg-gray-700 dark:border-gray-600"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    {/* === BAGIAN 2: STATUS & AKSES === */}
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-full">
                                                <Globe className="w-4 h-4 md:w-5 md:h-5 text-purple-600 dark:text-purple-400" />
                                            </div>
                                            <h3 className="text-lg md:text-xl font-bold text-primary dark:text-blue-400">
                                                Status Akses
                                            </h3>
                                        </div>
                                        <div className="h-px bg-gray-200 dark:bg-gray-700 w-full mb-6"></div>

                                        <FormField
                                            control={form.control}
                                            name="site_status"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                            {/* CARD: PUBLIC */}
                                                            <div
                                                                onClick={() => field.onChange("public")}
                                                                className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-md ${field.value === "public"
                                                                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                                                                    : "border-gray-200 dark:border-gray-600 hover:border-green-300"
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-3 mb-2">
                                                                    <Globe className={`w-5 h-5 ${field.value === "public" ? "text-green-600" : "text-gray-500"}`} />
                                                                    <h4 className="font-semibold text-sm md:text-base">Public (Terbuka)</h4>
                                                                </div>
                                                                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                                                    Situs dapat diakses oleh siapa saja. Tanggal batas waktu tetap harus diisi.
                                                                </p>
                                                            </div>

                                                            {/* CARD: PRIVATE */}
                                                            <div
                                                                onClick={() => field.onChange("private")}
                                                                className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-md ${field.value === "private"
                                                                    ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20"
                                                                    : "border-gray-200 dark:border-gray-600 hover:border-amber-300"
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-3 mb-2">
                                                                    <Lock className={`w-5 h-5 ${field.value === "private" ? "text-amber-600" : "text-gray-500"}`} />
                                                                    <h4 className="font-semibold text-sm md:text-base">Private (Tertutup)</h4>
                                                                </div>
                                                                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                                                    Akses dibatasi. Memerlukan batas waktu berlakunya status ini.
                                                                </p>
                                                            </div>

                                                            {/* CARD: MAINTENANCE */}
                                                            <div
                                                                onClick={() => field.onChange("maintenance")}
                                                                className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-md ${field.value === "maintenance"
                                                                    ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                                                                    : "border-gray-200 dark:border-gray-600 hover:border-red-300"
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-3 mb-2">
                                                                    <Wrench className={`w-5 h-5 ${field.value === "maintenance" ? "text-red-600" : "text-gray-500"}`} />
                                                                    <h4 className="font-semibold text-sm md:text-base">Maintenance</h4>
                                                                </div>
                                                                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                                                    Situs sedang dalam perbaikan. Akses ditutup sementara.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                                            <FormField
                                                control={form.control}
                                                name="site_status_until"
                                                render={({ field }) => (
                                                    <FormItem className="space-y-3">
                                                        <FormLabel className="text-sm md:text-base font-semibold flex items-center gap-2">
                                                            <CalendarClock className="w-4 h-4" />
                                                            Berlaku Hingga <span className="text-red-500">*</span>
                                                        </FormLabel>
                                                        <FormControl>
                                                            <div className="relative">
                                                                <Input
                                                                    type="datetime-local"
                                                                    step="1"
                                                                    className={`h-14 text-sm md:text-base border-gray-300 dark:bg-gray-700 dark:border-gray-600 ${form.formState.errors.site_status_until
                                                                        ? "border-red-500 ring-2 ring-red-100 focus-visible:ring-red-500"
                                                                        : "focus:border-primary focus:ring-primary"
                                                                        }`}
                                                                    {...field}
                                                                    value={field.value || ""}
                                                                />
                                                            </div>
                                                        </FormControl>
                                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                                            {form.formState.errors.site_status_until ? (
                                                                <span className="text-red-500 font-medium flex items-center gap-1">
                                                                    <AlertCircle className="w-3 h-3" /> Wajib diisi (Backend Requirement)
                                                                </span>
                                                            ) : (
                                                                <span>Tentukan tanggal berakhirnya status ini.</span>
                                                            )}
                                                        </div>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="registration_open"
                                                render={({ field }) => (
                                                    <FormItem className="space-y-3">
                                                        <FormLabel className="text-sm md:text-base font-semibold flex items-center gap-2">
                                                            <UserPlus className="w-4 h-4" />
                                                            Status Pendaftaran
                                                        </FormLabel>

                                                        <Select
                                                            onValueChange={(value) => field.onChange(value === "true")}
                                                            defaultValue={field.value ? "true" : "false"}
                                                            value={field.value ? "true" : "false"}
                                                        >
                                                            <FormControl>
                                                                <SelectTrigger style={{ height: '56px' }}
                                                                    className={`w-full h-14 px-4 text-sm md:text-base rounded-xl transition-all duration-200 border flex items-center focus:ring-2 focus:ring-offset-0
                                                                    ${field.value
                                                                            ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-500/50"
                                                                            : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                                                                        }`}
                                                                >
                                                                    <SelectValue placeholder="Pilih status..." />
                                                                </SelectTrigger>
                                                            </FormControl>

                                                            <SelectContent className="rounded-xl border-gray-200 dark:border-gray-700 shadow-xl">
                                                                <SelectItem
                                                                    value="true"
                                                                    className="cursor-pointer py-3 focus:bg-green-50 focus:text-green-700 dark:focus:bg-green-900/30 dark:focus:text-green-400"
                                                                >
                                                                    <div className="flex items-center gap-2 font-medium text-green-600 dark:text-green-400">
                                                                        <CheckCircle2 className="w-4 h-4" />
                                                                        <span>Dibuka (Open)</span>
                                                                    </div>
                                                                </SelectItem>

                                                                <SelectItem
                                                                    value="false"
                                                                    className="cursor-pointer py-3 focus:bg-red-50 focus:text-red-700 dark:focus:bg-red-900/30 dark:focus:text-red-400"
                                                                >
                                                                    <div className="flex items-center gap-2 font-medium text-red-600 dark:text-red-400">
                                                                        <XCircle className="w-4 h-4" />
                                                                        <span>Ditutup (Closed)</span>
                                                                    </div>
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>

                                                        <FormDescription>
                                                            {field.value
                                                                ? "Pengguna baru DIIZINKAN mendaftar."
                                                                : "Pendaftaran akun baru DITUTUP."}
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {/* === FOOTER BUTTON === */}
                                    <div className="pt-6">
                                        <Button
                                            type="submit"
                                            disabled={isSaving}
                                            className="w-full h-12 md:h-14 text-sm md:text-lg font-semibold bg-gradient-to-r from-primary to-primary hover:from-primary/90 hover:to-green-600 dark:from-blue-600 dark:to-green-600 transition-all duration-300 transform hover:scale-[1.01] shadow-lg rounded-xl"
                                        >
                                            {isSaving ? (
                                                <>
                                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                    Menyimpan...
                                                </>
                                            ) : (
                                                <>
                                                    <Save className="mr-2 h-5 w-5" />
                                                    Simpan Perubahan
                                                </>
                                            )}
                                        </Button>
                                    </div>

                                </form>
                            </Form>
                                </TabsContent>

                                <TabsContent value="notif" className="mt-0 outline-none focus:outline-none">
                                    <ConfigNotifTab />
                                </TabsContent>

                                <TabsContent value="services" className="mt-0 outline-none focus:outline-none">
                                    <ConfigServicesTab />
                                </TabsContent>

                                <TabsContent value="static" className="mt-0 outline-none focus:outline-none">
                                    <ConfigStaticTab />
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
}